import React from "react";

/**
 * Listening Orb — the signature element.
 *
 * Three concentric rings (outer dashed/faint, middle solid, inner segmented)
 * with a glowing radial core. States:
 *   idle      — calm cobalt
 *   listening — slow breathing pulse, cobalt
 *   thinking  — rings rotate, violet tint
 *   speaking  — warms to gold, steady glow
 */
export default function Orb({ size = 56, state = "idle", className = "" }) {
  const id = React.useId().replace(/:/g, "");
  const stateClass =
    state === "listening" ? "orb-listening"
    : state === "thinking" ? "orb-thinking"
    : state === "speaking" ? "orb-speaking"
    : "";

  // Tint overrides by state — Players Health palette
  // idle/listening: violet→teal brand; thinking: product teal; speaking: warm gold
  const outerColor = state === "thinking" ? "#4CD6BE" : state === "speaking" ? "#F2B65A" : "#7B6FE8";
  const middleColor = state === "thinking" ? "#4ECDC4" : state === "speaking" ? "#E5A852" : "#6B5CE7";
  const innerColor = state === "thinking" ? "#3BC0A9" : state === "speaking" ? "#B97F2A" : "#5849C7";

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={`${stateClass} ${className}`}
      style={{ display: "block", flexShrink: 0 }}
      data-testid="listening-orb"
    >
      <defs>
        <radialGradient id={`core-${id}`} cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#E9FBF8" />
          <stop offset="22%" stopColor="#A8E8DD" />
          <stop offset="60%" stopColor="#4ECDC4" />
          <stop offset="100%" stopColor="#6B5CE7" />
        </radialGradient>
        <radialGradient id={`core-warm-${id}`} cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#FFF1E0" />
          <stop offset="25%" stopColor="#F2B65A" />
          <stop offset="65%" stopColor="#E5A852" />
          <stop offset="100%" stopColor="#7A5217" />
        </radialGradient>
      </defs>

      {/* Outer ring — dashed, faint, "open/receiving" */}
      <circle
        className="orb-ring-outer"
        cx="32" cy="32" r="28"
        fill="none"
        stroke={outerColor}
        strokeWidth="1"
        strokeOpacity="0.55"
        strokeDasharray="2 4"
      />

      {/* Middle ring — solid */}
      <circle
        className="orb-ring-mid"
        cx="32" cy="32" r="22"
        fill="none"
        stroke={middleColor}
        strokeWidth="1.3"
        strokeOpacity="0.9"
      />

      {/* Inner ring — dashed/segmented */}
      <circle
        className="orb-ring-inner"
        cx="32" cy="32" r="16"
        fill="none"
        stroke={innerColor}
        strokeWidth="1.2"
        strokeOpacity="0.7"
        strokeDasharray="3 3"
      />

      {/* Glowing core */}
      <circle
        className="orb-core"
        cx="32" cy="32" r="9"
        fill={`url(#${state === "speaking" ? `core-warm-${id}` : `core-${id}`})`}
      />

      {/* Center hot dot */}
      <circle cx="32" cy="30" r="1.5" fill="#FFFFFF" opacity="0.92" />
    </svg>
  );
}
